import { Form } from "@circle-react-uikit/Form";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import type { FieldElement } from "@/react/types/Element";

import { useFieldElementRules } from "../shared/useFieldElementRules";

interface DropdownElementProps {
  defaultValue?: string;
  element: FieldElement;
  errors: Record<number, string>;
  fieldIndex: number;
}

export const DropdownElement = ({
  defaultValue,
  element,
  fieldIndex,
  errors,
}: DropdownElementProps) => {
  const { setError, setValue } = useFormContext();
  const idName = `elements[${fieldIndex}][id]`;
  const valueName = `elements[${fieldIndex}][value]`;
  const rules = useFieldElementRules(element);

  // Set initial value to empty string (or defaultValue if provided) so the prompt is shown and no Option is selected by default
  useEffect(() => {
    setValue(valueName, defaultValue || "", { shouldDirty: false });
  }, [valueName, setValue, defaultValue]);

  useEffect(() => {
    if (errors && errors[fieldIndex]) {
      setError(`elements[${fieldIndex}][value]`, {
        type: "server",
        message: errors[fieldIndex],
      });
    }
  }, [errors, fieldIndex, setError]);

  const options = (element.attributes.options || []).map(option => ({
    label: option,
    value: option,
  }));

  return (
    <>
      <Form.Item
        label={element.attributes.label}
        name={valueName}
        rules={rules}
        hideDescription
        hideBorder
        forceRequiredLabel={element.attributes.required}
        labelClassName="!mb-4 [overflow-wrap:anywhere]"
        errorClassName="form-group__error !mb-0"
      >
        <Form.SelectNative
          name={valueName}
          options={options}
          prompt={t("settings.forms.edit.defaults.dropdown_prompt")}
        />
      </Form.Item>
      <Form.Input
        wrapperClassName="hidden"
        type="hidden"
        name={idName}
        value={element.id}
      />
    </>
  );
};
